/**
 * ⚠ WARNING: YOU PROBABLY SHOULDN'T EDIT THIS FILE ⚠
 * Exports the "federated remote app interface" for this app and does some other
 * magic to enable HMR in a federated ecosystem.
 */

import React from 'react'
import { Channel } from '@ally/federator'
import { HostServices } from '@ally/federated-types'
import { render, unmountComponentAtNode } from 'react-dom'
import { name } from '../package.json'
import Application from './App'

/* eslint-disable global-require */

interface Module {
  hot?: { accept: () => void }
}

interface ChannelServices extends HostServices {
  done: () => void
}

interface RenderData {
  target: HTMLDivElement
  services: HostServices
}

const win = window as any
const key = Symbol.for(`@@ally/hot/${name}`)

const { hot } = module as Module

/**
 * Renders the application using ReactDOM.
 * If HMR is enabled (hot), then we store off all input to this function so
 * hot reloads can re-render the App with previously saved data.
 */
function renderer({ target, services }: RenderData, done?: () => void): void {
  // Note the require here takes a few 100ms the first time since it has to
  // load all this global scope stuff. Don't do it in production.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const App = hot && win[key] ? require('./App').default : Application
  if (hot) win[key] = { target, services }

  // Sets the display name of the <App /> component so it will display the name
  // of this package when viewed from React DevTools. Makes debugging easier.
  App.displayName = `RemoteApp(${name})`
  render(<App services={services} />, target, done)
}

/**
 * Sets up this application, called ONCE, after loading, but prior to `mount`.
 * Use this function to initialize things like Redux stores.
 */
export async function setup(): Promise<void> {
  // Do something?
}

/**
 * Mounts the application.
 * This function can be called more than once if the application is unmounted
 * then remounted again. Should populate the given target with your rendered
 * application.
 */
export function mount(target: HTMLDivElement, channel: Channel): void {
  channel.listen<ChannelServices>(({ done, ...services }) => {
    renderer({ target, services }, done)
  })
}

/**
 * Unmounts the application.
 * Called when your application is unmounted (0 or more times). Should cleanup
 * all memory references and essentially "destroy" your application.
 */
export function unmount(target: HTMLDivElement): void {
  if (win[key]) delete win[key]
  unmountComponentAtNode(target)
}

// For HMR, since we're delegating the invocation of `mount` to the host app, we
// have to handle re-mounting on a hot update. So stash away the data in
// `win[key]` for use in re-mounts.
if (hot) hot.accept?.()
if (win[key]) renderer(win[key])

// Registers this remote app with the host.
// When the host fetches this file, this is what provides the setup, mount, and
// unmount methods to it for deferred invocation.
win[Symbol.for('@ally/federator')].register(APP_BUILD_INFO.name, {
  info: APP_BUILD_INFO,
  setup,
  mount,
  unmount,
})
